/**
 * SEMH Strategy Selection Logic with Automatic Fallbacks
 * Implements eligibility checking and automatic substitution of appropriate fallbacks
 * when strategies don't match a child's communication/cognitive abilities.
 */

import { createAbilityProfile } from './abilityTiers.js';
import { SEMH_STRATEGIES, getStrategySpec } from './semhStrategies.js';

/**
 * Select SEMH strategies based on child's ability profile
 * @param {Object} responses - Questionnaire responses
 * @returns {Object} Object with ongoing and structured strategies
 */
export function selectSEMHStrategies(responses) {
  const profile = createAbilityProfile(responses);
  const selectedStrategies = {
    ongoing: [],
    structured: []
  };
  
  // Core always-on supports (always included)
  const coreStrategies = [
    'visual-schedules',
    'low-arousal-response', 
    'sensory-regulation-breaks'
  ];
  
  // Add core strategies
  coreStrategies.forEach(slug => {
    const strategy = getStrategySpec(slug);
    if (strategy) {
      selectedStrategies[strategy.category].push(slug);
    }
  });
  
  // Communication-informed choices based on expressive ability
  if (profile.expressiveTier === 'E' || profile.expressiveTier === 'D') {
    // Non-verbal or very limited communication
    selectedStrategies.structured.push('intensive-interaction', 'cause-and-effect');
    selectedStrategies.ongoing.push('functional-communication-training');
  } else if (profile.expressiveTier === 'C' || profile.expressiveTier === 'F') {
    // Limited phrases or echolalia
    selectedStrategies.ongoing.push('functional-communication-training');
    selectedStrategies.structured.push('social-stories'); // Keep short, highly visual
  }
  
  // Ability-gated strategies with automatic fallbacks
  const abilityGatedStrategies = [
    'break-card-system',
    'restorative-conversation',
    'token-reward-systems',
    'mindfulness-breathing',
    'social-skills-games',
    'check-in-check-out'
  ];
  
  abilityGatedStrategies.forEach(slug => {
    const strategy = getStrategySpec(slug);
    if (!strategy) return;
    
    if (strategy.isEligible(profile)) {
      // Strategy is eligible - add it
      selectedStrategies[strategy.category].push(slug);
    } else {
      // Strategy is ineligible - add fallbacks instead
      strategy.fallbacks.forEach(fallbackSlug => {
        const fallbackStrategy = getStrategySpec(fallbackSlug);
        if (fallbackStrategy && !selectedStrategies[fallbackStrategy.category].includes(fallbackSlug)) {
          selectedStrategies[fallbackStrategy.category].push(fallbackSlug);
        }
      });
    }
  });
  
  // Regulation style considerations
  if (['needs_co_regulation', 'escalates_quickly', 'shuts_down'].includes(profile.regulationIndependence)) {
    // Add movement breaks and co-regulation strategies
    if (!selectedStrategies.ongoing.includes('movement-breaks')) {
      selectedStrategies.ongoing.push('movement-breaks');
    }
    if (!selectedStrategies.ongoing.includes('co-regulation')) {
      selectedStrategies.ongoing.push('co-regulation');
    }
  }
  
  // Remove duplicates
  selectedStrategies.ongoing = [...new Set(selectedStrategies.ongoing)];
  selectedStrategies.structured = [...new Set(selectedStrategies.structured)];
  
  return selectedStrategies;
}

/**
 * Get detailed strategy selection with rationale
 * @param {Object} responses - Questionnaire responses
 * @returns {Object} Detailed selection with rationale and substitutions
 */
export function getDetailedSEMHSelection(responses) {
  const profile = createAbilityProfile(responses);
  const selection = selectSEMHStrategies(responses);
  const details = {
    profile,
    strategies: selection,
    substitutions: [],
    rationale: []
  };
  
  // Check for substitutions made
  const abilityGatedStrategies = [
    'break-card-system',
    'restorative-conversation', 
    'token-reward-systems',
    'mindfulness-breathing',
    'social-skills-games',
    'check-in-check-out'
  ];
  
  abilityGatedStrategies.forEach(slug => {
    const strategy = getStrategySpec(slug);
    if (!strategy) return;
    
    if (!strategy.isEligible(profile)) {
      // Strategy was substituted
      const fallbacksUsed = strategy.fallbacks.filter(fallbackSlug => {
        const fallbackStrategy = getStrategySpec(fallbackSlug);
        return fallbackStrategy && 
               (selection.ongoing.includes(fallbackSlug) || selection.structured.includes(fallbackSlug));
      });
      
      if (fallbacksUsed.length > 0) {
        details.substitutions.push({
          original: slug,
          fallbacks: fallbacksUsed,
          reason: getSubstitutionReason(slug, profile)
        });
      }
    }
  });
  
  // Generate rationale
  details.rationale = generateSelectionRationale(profile, selection, details.substitutions);
  
  return details;
}

/**
 * Get substitution reason for UI display
 * @param {string} originalSlug - Original strategy slug
 * @param {Object} profile - Child's ability profile
 * @returns {string} Human-readable reason for substitution
 */
function getSubstitutionReason(originalSlug, profile) {
  const reasons = {
    'break-card-system': 'This child currently needs adult-led coaching with visuals. Break requests are taught via FCT using gesture/symbols rather than verbal requests.',
    'restorative-conversation': 'This child currently needs adult-led coaching with visuals. Try Emotion Coaching + Social Stories instead.',
    'token-reward-systems': 'This child needs simpler reinforcement approaches. Using First/Then visuals and immediate praise instead.',
    'mindfulness-breathing': 'This child needs co-regulation support. Adult will model breathing and child can join if able.',
    'social-skills-games': 'This child needs 1:1 social support. Using parallel play and intensive interaction instead.',
    'check-in-check-out': 'This child needs adult observation and visual modelling. Using Zones modelling and emotion coaching with visuals.'
  };
  
  return reasons[originalSlug] || 'Strategy adapted to match child\'s current abilities.';
}

/**
 * Generate selection rationale
 * @param {Object} profile - Child's ability profile
 * @param {Object} selection - Selected strategies
 * @param {Array} substitutions - Substitutions made
 * @returns {string[]} Array of rationale statements
 */
function generateSelectionRationale(profile, selection, substitutions) {
  const rationale = [];
  
  // Communication-based rationale
  if (profile.expressiveTier === 'E') {
    rationale.push('Non-verbal profile: Focus on intensive interaction and cause-effect activities');
  } else if (profile.expressiveTier === 'F') {
    rationale.push('Echolalia profile: Using functional communication training and visual supports');
  } else if (['C', 'D'].includes(profile.expressiveTier)) {
    rationale.push('Limited communication: Emphasizing visual supports and functional communication');
  }
  
  // Receptive ability rationale
  if (profile.receptiveTier === 'E') {
    rationale.push('Minimal receptive language: Using visual schedules and intensive interaction');
  } else if (profile.receptiveTier === 'D') {
    rationale.push('Visual-dependent: All strategies adapted for visual support');
  }
  
  // Cognition rationale
  if (profile.cognitionTier === 'E') {
    rationale.push('Brief engagement: Using movement breaks and free flow activities');
  } else if (profile.cognitionTier === 'D') {
    rationale.push('Concrete learning: Strategies adapted for hands-on, concrete activities');
  }
  
  // Regulation rationale
  if (['needs_co_regulation', 'escalates_quickly', 'shuts_down'].includes(profile.regulationIndependence)) {
    rationale.push('Regulation support: Emphasizing co-regulation and low-arousal approaches');
  }
  
  // Substitution rationale
  if (substitutions.length > 0) {
    rationale.push(`${substitutions.length} strategy(ies) adapted to match child's current abilities`);
  }
  
  return rationale;
}

/**
 * Validate strategy selection against acceptance test criteria
 * @param {Object} responses - Questionnaire responses
 * @param {string} testCase - Test case identifier ('A', 'B', or 'C')
 * @returns {Object} Validation results
 */
export function validateSelection(responses, testCase) {
  const selection = selectSEMHStrategies(responses);
  const allStrategies = [...selection.ongoing, ...selection.structured];
  
  const testCriteria = {
    A: {
      // Non-verbal, no initiation
      mustInclude: ['intensive-interaction', 'cause-and-effect', 'functional-communication-training', 'visual-schedules', 'low-arousal-response', 'sensory-regulation-breaks'],
      mustNotInclude: ['break-card-system', 'restorative-conversation']
    },
    B: {
      // Limited phrases; 1-2 step; needs modelling
      mustInclude: ['break-card-system', 'social-stories', 'token-reward-systems', 'restorative-conversation'],
      mustNotInclude: []
    },
    C: {
      // Echolalia; single-step; high-repetition
      mustInclude: ['functional-communication-training', 'social-stories'],
      mustNotInclude: ['restorative-conversation']
    }
  };
  
  const criteria = testCriteria[testCase];
  if (!criteria) {
    return { valid: false, errors: ['Invalid test case'] };
  }
  
  const errors = [];
  
  // Check must include
  criteria.mustInclude.forEach(required => {
    if (!allStrategies.includes(required)) {
      errors.push(`Missing required strategy: ${required}`);
    }
  });
  
  // Check must not include
  criteria.mustNotInclude.forEach(forbidden => {
    if (allStrategies.includes(forbidden)) {
      errors.push(`Included forbidden strategy: ${forbidden}`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors,
    selectedStrategies: allStrategies
  };
}
